import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle, XCircle, Loader2, Gift, Home, Download } from "lucide-react";
import { BRAND } from "@/config/brand";
import brandLogo from "@/assets/regard-de-nature-logo.png";

type ConfirmStatus = "loading" | "success" | "already" | "error";

const NewsletterConfirm = () => {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<ConfirmStatus>("loading");
  const [firstName, setFirstName] = useState<string>("");
  const [giftPdfUrl, setGiftPdfUrl] = useState<string | null>(null);

  useEffect(() => {
    const confirmSubscription = async () => {
      const token = searchParams.get("token");

      if (!token) {
        setStatus("error");
        return;
      }

      try {
        const { data, error } = await supabase.functions.invoke("newsletter-confirm", {
          body: { token },
        });

        if (error) {
          console.error("Erreur confirmation:", error);
          setStatus("error");
          return;
        }

        if (data?.alreadyConfirmed) {
          setFirstName(data.firstName || "");
          setStatus("already");
        } else if (data?.success) {
          setFirstName(data.firstName || "");
          setGiftPdfUrl(data.giftPdfUrl || null);
          setStatus("success");
        } else {
          setStatus("error");
        }
      } catch (err) {
        console.error("Erreur:", err);
        setStatus("error");
      }
    };

    confirmSubscription();
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-gradient-hero flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-xl border-0">
        <CardContent className="p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <img src={brandLogo} alt={BRAND.name.full} className="w-16 h-auto mx-auto mb-4" />
            <div className="font-cinzel font-bold">
              <span className="text-secondary text-xl">{BRAND.name.line1}</span>
              <span className="text-primary text-xl"> {BRAND.name.line2}</span>
            </div>
            <p className="text-[10px] font-montserrat uppercase tracking-wide mt-1">
              <span className="text-primary">{BRAND.baseline.parts.text1}</span>
              <span className="text-secondary"> {BRAND.baseline.parts.separator} </span>
              <span className="text-primary">{BRAND.baseline.parts.text2}</span>
            </p>
          </div>

          {status === "loading" && (
            <div className="text-center">
              <Loader2 className="w-12 h-12 text-primary animate-spin mx-auto mb-4" />
              <p className="text-muted-foreground font-montserrat">Confirmation en cours...</p>
            </div>
          )}

          {status === "success" && (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h1 className="text-2xl font-cinzel font-bold text-primary mb-3">
                Inscription confirmée !
              </h1>
              <p className="text-muted-foreground font-montserrat mb-6">
                Merci {firstName} ! Vous êtes maintenant inscrit(e) à la newsletter de {BRAND.name.full}.
              </p>

              {giftPdfUrl && (
                <div className="bg-muted/50 rounded-lg p-6 mb-6 border border-secondary/30">
                  <Gift className="w-10 h-10 text-secondary mx-auto mb-3" />
                  <p className="text-sm font-semibold text-primary mb-2">
                    Votre cadeau de bienvenue
                  </p>
                  <p className="text-xs text-muted-foreground mb-4 italic">
                    "Mieux comprendre son terrain – Les bases pour écouter son corps"
                  </p>
                  <Button asChild className="bg-gradient-primary">
                    <a href={giftPdfUrl} target="_blank" rel="noopener noreferrer">
                      <Download className="w-4 h-4 mr-2" />
                      Télécharger mon guide
                    </a>
                  </Button>
                </div>
              )}

              <Button asChild variant="outline" className="w-full">
                <Link to="/">
                  <Home className="w-4 h-4 mr-2" />
                  Retour au site
                </Link>
              </Button>
            </div>
          )}

          {status === "already" && (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-blue-600" />
              </div>
              <h1 className="text-2xl font-cinzel font-bold text-primary mb-3">
                Déjà confirmé !
              </h1>
              <p className="text-muted-foreground font-montserrat mb-6">
                {firstName}, votre inscription est déjà confirmée. Vous recevez déjà nos newsletters.
              </p>
              <Button asChild variant="outline" className="w-full">
                <Link to="/">
                  <Home className="w-4 h-4 mr-2" />
                  Retour au site
                </Link>
              </Button>
            </div>
          )}

          {status === "error" && (
            <div className="text-center">
              <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-10 h-10 text-red-600" />
              </div>
              <h1 className="text-2xl font-cinzel font-bold text-primary mb-3">
                Lien invalide
              </h1>
              <p className="text-muted-foreground font-montserrat mb-6">
                Ce lien de confirmation n'est pas valide ou a expiré. Veuillez vous réinscrire à la newsletter.
              </p>
              <Button asChild variant="outline" className="w-full">
                <Link to="/">
                  <Home className="w-4 h-4 mr-2" />
                  Retour au site
                </Link>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default NewsletterConfirm;
